import { useCallback, useRef, useState } from 'react'
import { useToast } from '@chakra-ui/react'
import API from '../../API/API'

const authHeaders = () => ({
    authorization: `Bearer ${window.localStorage.getItem('accessToken')}`,
    'content-type': 'application/json'
})

/** The endpoint returns either { refreshToken } or the bare token string. */
const readToken = (data) => (data && data.refreshToken ? data.refreshToken : data) || ''

/**
 * Owns the client refresh token: fetching it, copying it, deleting it, and running
 * the popup authorization flow that mints a new one.
 */
const useRefreshToken = () => {
    const [isOpen, setIsOpen] = useState(false)
    const [token, setToken] = useState('')
    const [hasToken, setHasToken] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isGenerating, setIsGenerating] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)

    const pollTimer = useRef(null)
    const toast = useToast()

    const notify = useCallback((title, description, status) => {
        toast({ title, description, status, duration: 3000, isClosable: true, position: 'top-right' })
    }, [toast])

    const open = useCallback(() => {
        setIsOpen(true)
        setIsConfirmingDelete(false)
        setIsLoading(true)

        API.get('/Client/ClientRefreshToken', { headers: authHeaders() })
            .then((response) => {
                const value = response.status === 200 ? readToken(response.data) : ''
                setToken(value)
                setHasToken(Boolean(value))
                setIsLoading(false)
            })
            .catch((error) => {
                setToken('')
                setHasToken(false)
                setIsLoading(false)
                notify('Error', 'Unable to fetch the token', 'error')
                // eslint-disable-next-line no-console
                console.error('Refresh token fetch failed', error)
            })
    }, [notify])

    const close = useCallback(() => {
        if (pollTimer.current) {
            clearInterval(pollTimer.current)
            pollTimer.current = null
        }
        setIsOpen(false)
        setIsConfirmingDelete(false)
    }, [])

    const copy = useCallback(() => {
        navigator.clipboard.writeText(token)
            .then(() => notify('Success', 'Token copied to clipboard', 'success'))
            .catch(() => notify('Error', 'Unable to copy the token', 'error'))
    }, [token, notify])

    const requestDelete = useCallback(() => setIsConfirmingDelete(true), [])
    const cancelDelete = useCallback(() => setIsConfirmingDelete(false), [])

    const confirmDelete = useCallback(() => {
        setIsDeleting(true)

        API.delete('/Client/DeleteToken', { headers: authHeaders() })
            .then(() => {
                setToken('')
                setHasToken(false)
                setIsDeleting(false)
                setIsConfirmingDelete(false)
                notify('Success', 'Token has been deleted', 'success')
            })
            .catch((error) => {
                setIsDeleting(false)
                setIsConfirmingDelete(false)
                notify('Error', 'Failed to delete token', 'error')
                // eslint-disable-next-line no-console
                console.error('Refresh token delete failed', error)
            })
    }, [notify])

    const generate = useCallback(() => {
        setIsGenerating(true)

        API.get('/User/Authorize', { headers: authHeaders() })
            .then((response) => {
                if (response.status !== 200) {
                    setIsGenerating(false)
                    return
                }
                const popup = window.open(response.data, '', 'width=500,height=500')
                if (!popup) {
                    setIsGenerating(false)
                    notify('Error', 'Allow pop-ups for this site to authorize', 'error')
                    return
                }
                // The popup is on another origin, so closing is the only signal we get.
                pollTimer.current = setInterval(() => {
                    if (!popup.closed) {
                        return
                    }
                    clearInterval(pollTimer.current)
                    pollTimer.current = null

                    API.get('/Client/ClientRefreshToken', { headers: authHeaders() })
                        .then((tokenResponse) => {
                            const value = readToken(tokenResponse.data)
                            if (value) {
                                setToken(value)
                                setHasToken(true)
                                notify('Success', 'New token loaded', 'success')
                            }
                            setIsGenerating(false)
                        })
                        .catch((error) => {
                            setIsGenerating(false)
                            // eslint-disable-next-line no-console
                            console.error('Refresh token reload failed', error)
                        })
                }, 500)
            })
            .catch((error) => {
                setIsGenerating(false)
                notify('Error', 'Failed to start authorization', 'error')
                // eslint-disable-next-line no-console
                console.error('Authorization start failed', error)
            })
    }, [notify])

    return {
        isOpen,
        token,
        hasToken,
        isLoading,
        isGenerating,
        isDeleting,
        isConfirmingDelete,
        open,
        close,
        copy,
        requestDelete,
        cancelDelete,
        confirmDelete,
        generate
    }
}

export default useRefreshToken
